import { useCallback, useEffect, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';

import { ActivityCenterContext } from '@xing-com/hub';
import type { ActivityCenterOpenSource } from '@xing-com/hub';

import { shouldOpenActivityCenterHelper } from './activity-center-rules';
import { ActivityListener } from './activity-listener';
import { useTotals } from './use-totals';
import { getOriginFromPath } from './utils';

export const ActivityCenterModule: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const origin = getOriginFromPath();

  const [
    wasActivityCenterButtonRenderedOnce,
    setWasActivityCenterButtonRenderedOnce,
  ] = useState(false);

  const [activityCenterState, setActivityCenterState] = useState<{
    isVisible: boolean;
    openSource?: ActivityCenterOpenSource;
  }>({ isVisible: false });
  const [shouldOpenExternal, setShouldOpenExternal] = useState(false);
  const openActivityCenter = useCallback(
    (openSource?: ActivityCenterOpenSource): void => {
      setActivityCenterState({ isVisible: true, openSource });
    },
    []
  );
  const closeActivityCenter = useCallback((): void => {
    setActivityCenterState({ isVisible: false });
  }, []);
  const {
    total,
    totalByEvents,
    previousTotalByEvents,
    refetch,
    setTotalByEvents,
  } = useTotals(!wasActivityCenterButtonRenderedOnce);

  useEffect(() => {
    const previousJobBookmarks = previousTotalByEvents?.jobBookmarks;

    if (typeof previousJobBookmarks === 'number') {
      const shouldOpen = shouldOpenActivityCenterHelper(
        previousJobBookmarks,
        totalByEvents,
        origin
      );

      if (shouldOpen) {
        setShouldOpenExternal(true);
      } else {
        setShouldOpenExternal(false);
      }
    }

    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [totalByEvents, origin]);

  return (
    <ActivityCenterContext.Provider
      value={{
        setWasActivityCenterButtonRenderedOnce,
        isActivityCenterVisible: activityCenterState.isVisible,
        total,
        totalByEvents,
        setTotalByEvents,
        activityCenterOpenSource: activityCenterState.openSource,
        openActivityCenter,
        closeActivityCenter,
        origin,
        shouldOpenExternal,
        setShouldOpenExternal,
      }}
    >
      {children}
      {wasActivityCenterButtonRenderedOnce ? (
        <ActivityListener onMessageReceived={refetch} />
      ) : null}
    </ActivityCenterContext.Provider>
  );
};
